import React, { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { api } from "./axiosInstance";

const tierDisplayName = (tier) => {
    const names = { productUpdates: "Product Updates", marketing: "Tips and Guides" };
    return names[tier] || tier;
};

const EmailPreferences = () => {
    const [searchParams] = useSearchParams();
    const [status, setStatus] = useState("loading");
    const [preferences, setPreferences] = useState({ productUpdates: true, marketing: true });
    const [errorMessage, setErrorMessage] = useState("");
    const [unsubscribedTier, setUnsubscribedTier] = useState("");

    useEffect(() => {
        const token = searchParams.get("token");
        const action = searchParams.get("action");

        if (!token) {
            setStatus("error");
            setErrorMessage("This link is invalid or has expired. Sign in to manage your preferences.");
            return;
        }

        if (action === "unsubscribe") {
            api.get(`/email-preferences/unsubscribe?token=${token}`)
                .then((res) => {
                    setPreferences(res.data.preferences);
                    setUnsubscribedTier(res.data.tier);
                    setStatus("unsubscribed");
                })
                .catch(() => {
                    setStatus("error");
                    setErrorMessage("This link is invalid or has expired. Sign in to manage your preferences.");
                });
        } else {
            api.get(`/email-preferences?token=${token}`)
                .then((res) => {
                    setPreferences(res.data.preferences);
                    setStatus("loaded");
                })
                .catch(() => {
                    setStatus("error");
                    setErrorMessage("This link is invalid or has expired. Sign in to manage your preferences.");
                });
        }
    }, [searchParams]);

    const handleSave = async () => {
        setStatus("saving");
        try {
            await api.post("/email-preferences", {
                token: searchParams.get("token"),
                preferences,
            });
            setStatus("saved");
            setTimeout(() => setStatus("loaded"), 3000);
        } catch {
            setStatus("error");
            setErrorMessage("Something went wrong. Please try again or sign in to manage your preferences.");
        }
    };

    const showForm = ["loaded", "unsubscribed", "saving", "saved"].includes(status);

    return (
        <div className="min-h-screen flex items-center justify-center bg-darkblue text-lightgray">
            <div className="w-full max-w-md bg-bluegray shadow-lg rounded-lg p-6">
                <h1 className="text-3xl font-bold text-orange mb-6 text-center">Email Preferences</h1>

                {status === "loading" && (
                    <p className="text-center text-lightgray">Loading your preferences...</p>
                )}

                {status === "error" && (
                    <p className="text-red-400 text-center">{errorMessage}</p>
                )}

                {status === "unsubscribed" && (
                    <div className="text-green-400 mb-4 text-center">
                        <p className="font-bold">You've been unsubscribed.</p>
                        <p className="text-sm">
                            You'll no longer receive {tierDisplayName(unsubscribedTier)} emails. Adjust your other preferences below.
                        </p>
                    </div>
                )}

                {showForm && (
                    <>
                        <div className="space-y-4 mb-6">
                            {/* Product Updates toggle */}
                            <div>
                                <label className="flex items-center justify-between cursor-pointer">
                                    <span className="text-sm font-bold text-lightgray">Product Updates</span>
                                    <div className="relative">
                                        <input
                                            type="checkbox"
                                            className="sr-only peer"
                                            checked={preferences.productUpdates}
                                            onChange={() =>
                                                setPreferences((p) => ({ ...p, productUpdates: !p.productUpdates }))
                                            }
                                        />
                                        <div className="w-11 h-6 bg-deepblue rounded-full peer peer-checked:bg-orange transition-colors" />
                                        <div className="absolute left-1 top-1 w-4 h-4 bg-white rounded-full transition-transform peer-checked:translate-x-5" />
                                    </div>
                                </label>
                                <p className="text-xs text-lightgray mt-1">New features, platform changes, and improvements.</p>
                            </div>

                            {/* Marketing toggle */}
                            <div>
                                <label className="flex items-center justify-between cursor-pointer">
                                    <span className="text-sm font-bold text-lightgray">Tips and Guides</span>
                                    <div className="relative">
                                        <input
                                            type="checkbox"
                                            className="sr-only peer"
                                            checked={preferences.marketing}
                                            onChange={() =>
                                                setPreferences((p) => ({ ...p, marketing: !p.marketing }))
                                            }
                                        />
                                        <div className="w-11 h-6 bg-deepblue rounded-full peer peer-checked:bg-orange transition-colors" />
                                        <div className="absolute left-1 top-1 w-4 h-4 bg-white rounded-full transition-transform peer-checked:translate-x-5" />
                                    </div>
                                </label>
                                <p className="text-xs text-lightgray mt-1">Career tips, guides, and promotional content.</p>
                            </div>

                            {/* Transactional (disabled, always-on) */}
                            <div>
                                <label className="flex items-center justify-between cursor-not-allowed opacity-50">
                                    <span className="text-sm font-bold text-lightblue">Account Emails</span>
                                    <div className="relative">
                                        <input
                                            type="checkbox"
                                            className="sr-only peer"
                                            checked
                                            disabled
                                            aria-disabled="true"
                                            title="Account emails cannot be disabled"
                                        />
                                        <div className="w-11 h-6 bg-deepblue rounded-full peer peer-checked:bg-orange transition-colors" />
                                        <div className="absolute left-1 top-1 w-4 h-4 bg-white rounded-full transition-transform peer-checked:translate-x-5" />
                                    </div>
                                </label>
                                <p className="text-xs text-lightgray mt-1">
                                    Password resets, email verification, and invitations. These cannot be disabled.
                                </p>
                            </div>
                        </div>

                        <button
                            onClick={handleSave}
                            disabled={status === "saving"}
                            className={`w-full py-2 px-4 bg-orange text-white font-bold rounded hover:bg-deeporange ${
                                status === "saving" ? "opacity-50 cursor-not-allowed" : ""
                            }`}
                        >
                            {status === "saving" ? "Saving..." : "Save Preferences"}
                        </button>

                        {status === "saved" && (
                            <p className="text-green-400 text-center mt-2 text-sm">Preferences saved.</p>
                        )}
                    </>
                )}
            </div>
        </div>
    );
};

export default EmailPreferences;
